import React, {
  memo,
  useState,
  useEffect,
  useMemo,
  forwardRef,
  Ref,
  useImperativeHandle,
} from 'react';
import { Transfer, Button } from 'tea-component';
import { useDeepCompareEffect } from 'ahooks';
import { getHomePageSearchList } from '@gp/services/projectDetail';
import { reportOperationLog } from '@gp/services/global';
import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import SourceTable from './components/SourceTable';
import TargetTable from './components/TargetTable';
import { isEmpty } from 'lodash-es';
import KeywordsSelect from '../KeywordSelect';
import { followSearchPullDown } from '@/pages/intelligence/services/following';
import './index.scss';

interface TransferTableProps {
  defaultData?: any; // 默认传进来的数据
  searchReportOperationLogBasicMsg: any;
  [kewNmae: string]: any;
}

export interface TransferTableForwardProps {
  selectList: any[] | null;
}

const DEFAULT_PROPS: TransferTableProps = {
  defaultData: null,
  searchReportOperationLogBasicMsg: {},
};

/**
 * 穿梭框模块
 */
const index: React.FC<TransferTableProps> = memo(
  forwardRef((props, ref: Ref<TransferTableForwardProps | HTMLElement>) => {
    const { defaultData, searchReportOperationLogBasicMsg, allCountryFlag, upData } = props;

    const platformIcon: { [keyName: number]: string } = {
      1: 'mobile',
      2: 'pc',
      3: 'console',
    };
    // 转换数据字段
    const computedDefaultData = useMemo(() => {
      return defaultData?.reduce((o: any[], t: any) => {
        o.push({
          cover: t?.cover || '',
          game_id: t?.unified_id || '',
          entity_name: t?.name || '-',
          entity_type: platformIcon?.[t?.game_type] || '',
          publisher: t?.country_en_name || '-',
          country_en_name_abbr: t?.country_en_name_abbr || '-',
        });
        return o;
      }, []);
    }, [defaultData]);

    const [loading, setLoading] = useState<boolean>(false);
    const [tableList, setTableList] = useState<any[]>([]);
    // 选中项密匙
    const [targetKeys, setTargetKeys] = useState<string[]>([]);
    // 搜索内容
    const [inputValue, setInputValue] = useState<string>('');
    // 默认传进来的的数据做一次储存，之后多次搜索要把前一次右侧表格的数据储存在这里
    const [newDefaultData, setNewDefaultData] = useState<any[]>(computedDefaultData);
    // 当前右侧表格数据
    const [currentTargetTableList, setCurrentTargetTableList] = useState<any[]>([]);

    // 选择左侧搜索的数据和删除右侧数据时，计算右侧表格数据
    const targetTableList = useMemo(() => {
      const selectTableList = tableList.filter((i: any) =>
        targetKeys.includes(i.game_id as string),
      );
      if (currentTargetTableList && currentTargetTableList?.length > 0)
        selectTableList.unshift(...currentTargetTableList);
      // 去重
      const uniqueSelectTableList: any[] = [];
      selectTableList.forEach((item: any) => {
        const has = uniqueSelectTableList.some((t: any) => t.game_id === item.game_id);
        !has && uniqueSelectTableList.push(item);
      });
      return uniqueSelectTableList;
    }, [tableList, targetKeys, currentTargetTableList]);

    // 暴露给父组件的方法或属性
    useImperativeHandle(
      ref,
      () => ({
        selectList: targetTableList,
      }),
      [targetTableList, targetKeys],
    );

    // 搜索请求接口
    const getSearchList = async (val: string) => {
      setInputValue(val);
      if (!val?.trim()) return;
      setLoading(true);
      try {
        const params = {
          system_type: 'pc',
          entity_type: 'all',
          ids: [],
          keyword: val,
          page: 1,
          page_size: 10,
        };

        const res: any = await getHomePageSearchList(params);
        val &&
          reportOperationLog(
            {
              ...searchReportOperationLogBasicMsg,
              query: val,
            },
            'search',
          );
        // 过滤是公司的数据
        const filterList = res?.list?.filter((t: any) => t?.entity_type !== 'company') || [];
        setLoading(false);
        setTableList(filterList);
      } catch (error) {}
    };

    // 用户进行输入搜索后
    useEffect(() => {
      if (!inputValue) return;
      // 每次进行搜索要储存一次右边选中项数据
      setNewDefaultData(targetTableList);
    }, [inputValue]);

    useEffect(() => {
      setCurrentTargetTableList(newDefaultData);
    }, [newDefaultData]);

    useEffect(() => {
      if (targetKeys) {
        upData?.();
      }
    }, [targetKeys]);

    return (
      <div className="dbg-similar-modal">
        <Transfer
          className="dbg-similar-modal-transfer"
          // className="globalpedia-custom-scrollbar"
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title="Add"
              className="dbg-similar-leftcell"
              tip=""
              header={
                <div className="dbg-similar-leftcell-header">
                  <KeywordsSelect
                    searchPlaceholder={'Search any Game'}
                    keywordsFetcher={followSearchPullDown}
                    value={inputValue}
                    entityType={'all'}
                    onChange={getSearchList}
                  />
                  {/* <div className="dbg-similar-leftcell-text">Similar Games</div> */}
                </div>
              }
            >
              {loading ? (
                <main style={{ height: '100%' }}>
                  <Loading loading />
                </main>
              ) : tableList?.length > 0 ? (
                <SourceTable
                  allCountryFlag={allCountryFlag}
                  dataSource={tableList}
                  targetKeys={targetKeys}
                  onChange={(keys: string[]) => setTargetKeys(keys)}
                />
              ) : (
                <NoData
                  text={
                    inputValue ? 'Sorry, no result found' : 'Please search for a game and add it.'
                  }
                />
              )}
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              className="dbg-similar-rightcell"
              scrollable={false}
              title={
                <div className="dbg-similar-transfer-title">
                  <div className="dbg-similar-transfer-select">{`Selected (${targetTableList?.length})`}</div>
                  {!isEmpty(targetTableList) && (
                    <Button
                      className="dbg-similar-transfer-btn"
                      type="link"
                      onClick={() => {
                        setCurrentTargetTableList([]);
                        setTargetKeys([]);
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              }
              header={<div className="dbg-similar-rightcell-title">Games</div>}
            >
              {targetTableList && targetTableList.length > 0 ? (
                <TargetTable
                  allCountryFlag={allCountryFlag}
                  dataSource={targetTableList}
                  onDragEnd={(records: any) => setCurrentTargetTableList(records)}
                  onRemove={(key: string) => {
                    // 当是默认列表的数据时删除默认选中的数据
                    const newCurrentTargetTableList = [...currentTargetTableList].filter(
                      (t: any) => t?.game_id !== key,
                    ); // 过滤默认数据
                    setCurrentTargetTableList(newCurrentTargetTableList);
                    // 当是搜索列表的数据时删除搜索选中的数据
                    setTargetKeys(targetKeys.filter((i) => i !== key));
                  }}
                />
              ) : (
                <NoData text="Please select a game." />
              )}
            </Transfer.Cell>
          }
        />
      </div>
    );
  }),
);

/**
 * props默认值
 */
index.defaultProps = DEFAULT_PROPS;

export default index;
